import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TimeOffModuleMenuService } from '@app/modules/time-off-v3/services/time-off-module-menu.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Department } from '@models/company/department.model';
import { TimeOffRequestApproval } from '@models/time-off-v3/time-off-request-approval.model';
import { TimeOffRequest } from '@models/time-off-v3/time-off-request.model';
import { TimeOffCommentTypesEnum } from '@time-off-v3/enum/time-off-comment-types.enum';
import { GeneralTimeOffRequestTable } from '@time-off-v3/services/general-time-off-requests-table/general-time-off-request-table';

@Component({
    templateUrl: './request-index.template.html',
})
export class RequestIndexView implements OnInit {
    timeOffRequestsTable: GeneralTimeOffRequestTable;
    isLoading = false;
    isReady = false;
    isDenyRequestCommentModalOpen = false;
    selectedTimeOffRequest: TimeOffRequest = new TimeOffRequest();

    constructor(
        private auth: AuthService,
        private router: Router,
        private notify: NotifyService,
        private analyticService: AnalyticService,
        public tabMenuService: TimeOffModuleMenuService
    ) {}

    async ngOnInit(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffAllRequests);

        this.toggleLoading();
        const [departments] = await Department.param('company', this.auth.company.id).orderBy('name', 'ASC').get();
        this.timeOffRequestsTable = new GeneralTimeOffRequestTable(this.auth, departments);
        this.isReady = true;
        this.toggleLoading();
    }

    onCloseDenyRequestModal(): void {
        this.isDenyRequestCommentModalOpen = false;
        return;
    }

    async onConfirmDenyRequest(comment?: string): Promise<void> {
        this.toggleLoading();

        try {
            const timeOffRequestApproval = this.findTimeOffRequestApproval(this.selectedTimeOffRequest);

            timeOffRequestApproval.type = TimeOffCommentTypesEnum.DENIED;
            timeOffRequestApproval.comment = comment;

            await timeOffRequestApproval.deny();

            if (comment) {
                this.analyticService.trackEvent(
                    AnalyticEvents.TimeOffRequestsMeatballDenyAddCommentClickDenyRequestToSave
                );
            }

            await this.timeOffRequestsTable.reload();
            this.notify.success('time-off-v3.requestHasBeenDeniedSuccessfully');
            this.analyticService.trackEvent(AnalyticEvents.TimeOffRequestsMeatballDeny);
        } catch (e) {
            this.notify.error(e.message);
        }

        this.toggleLoading();
        this.isDenyRequestCommentModalOpen = false;
    }

    async onMenuAction(event: MenuColumnEvent): Promise<void> {
        const { menuItem, data } = event;

        this.selectedTimeOffRequest = data as TimeOffRequest;

        switch (menuItem) {
            case GeneralTimeOffRequestTable.editOption:
                this.router.navigate(['/', 'time-off', 'v3', 'requests', this.selectedTimeOffRequest.id, 'edit']);
                break;
            case GeneralTimeOffRequestTable.approveOption:
                this.toggleLoading();
                await this.findTimeOffRequestApproval(this.selectedTimeOffRequest).approve();
                this.toggleLoading();
                await this.timeOffRequestsTable.reload();
                this.notify.success('time-off-v3.requestHasBeenApprovedSuccessfully');
                this.analyticService.trackEvent(AnalyticEvents.TimeOffRequestsMeatballApprove);
                break;
            case GeneralTimeOffRequestTable.denyOption:
                this.isDenyRequestCommentModalOpen = true;
        }
    }

    private findTimeOffRequestApproval(timeOffRequest: TimeOffRequest): TimeOffRequestApproval {
        return timeOffRequest.timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps
            .find((requestApprovalFlowStep) =>
                requestApprovalFlowStep.timeOffRequestApprovals
                    .map((requestApproval) => requestApproval.approver.id)
                    .includes(this.auth.employee.id)
            )
            .timeOffRequestApprovals.find((requestApproval) => requestApproval.approver.is(this.auth.employee));
    }

    private toggleLoading(): void {
        this.isLoading = !this.isLoading;
    }
}
